<template>
  <div>
    <div
      class="w-full mt-8 relative md:max-w-screen-xl md:mx-auto md:px-4 overflow-hidden"
    >
      <div
        class="relative rounded-md overflow-hidden min-h-48"
        :class="bannerClass"
      >
        <strapi-image
          :image="component.backgroundImage"
          class="absolute inset-0 w-full h-full rounded-md shadow-md z-0 pointer-events-none"
          as-background
        />

        <!-- Banner with promo bubble -->
        <div
          v-if="component.promoText"
          class="relative z-2 p-4 md:p-8 flex flex-wrap items-center justify-between max-w-screen-xl mx-auto"
        >
          <div class="w-full md:flex-1 lg:w-1/2">
            <h2 class="mb-0 uppercase">
              {{ component.title }}
            </h2>
            <p class="mt-4 tracking-[1.5px]">
              <strong>{{ component.text }}</strong>
            </p>
            <a
              v-for="button in component.buttons"
              :key="button.id"
              :href="button.link"
              :target="button.newTab ? '_blank' : '_self'"
              class="inline-block mt-4 px-8 py-1.5 rounded-md font-bold uppercase tracking-wider hover:opacity-90 transition-opacity no-underline"
              :class="strapiBtnClass(button.type)"
            >
              {{ button.text }}
            </a>
          </div>

          <div
            class="ml-auto mt-4 md:mt-0 rounded-full size-27 md:size-36 p-[0.2rem] shadow-md bg-linear-[125deg] from-depth-3 from-10% to-depth-4 to-80%"
          >
            <div
              class="size-25 md:size-34 rounded-full uppercase text-center border border-mouse flex flex-col items-center justify-center md:tracking-[2.4px]"
            >
              <span class="font-bold">{{ component.promoText }}</span>
            </div>
          </div>
        </div>

        <!-- Standard banner content -->
        <div
          v-else
          class="relative z-2 h-full py-8 lg:py-16 px-4 md:px-8 lg:px-20 max-w-screen-xl mx-auto"
        >
          <h3 class="mb-0 uppercase text-xl md:text-2xl">
            {{ component.title }}
          </h3>
          <h2 class="tracking-[1.5px] text-2xl md:text-3xl mb-4 uppercase">
            {{ component.text }}
          </h2>
          <div class="mt-4">
            <a
              v-for="button in component.buttons"
              :key="button.id"
              :href="button.link"
              :target="button.newTab ? '_blank' : '_self'"
              class="inline-block w-auto px-8 py-2.5 rounded-md font-bold uppercase tracking-wider hover:opacity-90 transition-opacity no-underline"
              :class="strapiBtnClass(button.type)"
            >
              {{ button.text }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StrapiBanner } from '~/apollo/types/types';

defineProps<{
  component: StrapiBanner;
  bannerClass?: string;
  fullWidthOnMobile?: boolean;
}>();

const btnTypeClasses: Record<string, string> = {
  primary: 'bg-brand text-onyx',
  secondary: 'bg-mouse text-depth-0',
  'outline-primary':
    'border border-brand text-brand hover:bg-brand hover:text-onyx',
};

function strapiBtnClass(type: string): string {
  return btnTypeClasses[type] ?? btnTypeClasses.primary;
}
</script>
